// angular modules
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { ErrorHandler, NgModule } from "@angular/core";
import { FlexLayoutModule } from "@angular/flex-layout";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatIconRegistry } from "@angular/material/icon";
import { BrowserModule, DomSanitizer } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { AppRoutingModule } from "./app-routing.module";

// ngrx Store
import { EffectsModule } from "@ngrx/effects";
import {
    RouterStateSerializer,
    StoreRouterConnectingModule,
} from "@ngrx/router-store";
import { MetaReducer, StoreModule } from "@ngrx/store";
import { CustomSerializer, effects, reducers } from "./core/router-store";

// not used in production

// in-house directives
import { TrimInputDirective } from "./core/directives/trim-input/trim-input.directive";

// shared modules
import { CoreModule } from "./core/core.module";

// shared services
import { UsherPayloadHelper } from "#core/helpers/usher-payload.helper";
import { LocalStorageCache } from "#core/services/local-storage-cache.service";
import { LoggerService } from "#core/services/logger.service";
import { DurationFormat } from "#helpers/durationFormat.helper";
import { LanguageHelper } from "#helpers/language.helper";
import { ProficiencyReturn } from "#helpers/proficiencyReturn.helper";
import { StatesHelper } from "#helpers/states.helper";
import { TimezoneHelper } from "#helpers/timezone.helper";
import { AuthHttpInterceptor } from "#interceptors/auth.interceptor";
import { CacheErrorInterceptor } from "#interceptors/cache.interceptor";
import { LocalStorage } from "#services/local-storage.service";

// feature modules
import { LoginModule } from "./login/login.module";

// 3rd party modules
import {
    OwlDateTimeModule,
    OwlNativeDateTimeModule,
} from "@danielmoncada/angular-datetime-picker";
import { OwlMomentDateTimeModule } from "@danielmoncada/angular-datetime-picker-moment-adapter";
import { NgIdleModule } from "@ng-idle/core";
import { DefaultFallbackStrategy, DefaultInterceptor, DefaultMissingHandler, DefaultTranspiler, TRANSLOCO_FALLBACK_STRATEGY, TRANSLOCO_INTERCEPTOR, TRANSLOCO_MISSING_HANDLER, TRANSLOCO_TRANSPILER } from "@jsverse/transloco";
import { ENV } from "cli-types";
import { FileUploadModule } from "ng2-file-upload";
import { ClipboardModule } from "ngx-clipboard";
import { QuillModule } from "ngx-quill";

// containers
import { AppComponent } from "./app.component";

// environment (basic prod or not)
import { environment, extModules } from "../environments/environment";

// store
import { RestModule } from "@carnegie-learning/data-access-angular";
import { withClToken2AsCookieInterceptor } from "@carnegie-learning/data-access-angular-fido";

// Reset the store whenever user is logged out
export function logout(reducer: (arg0: any, arg1: any) => any) {
    // TODO: Something better? This feels crappy, but it works.
    return function (state: any, action: { type: string }) {
        return reducer(
            action.type === "[Login] Logout Meta" // In ngrx v8, can only be matched by action text
                ? undefined
                : state,
            action
        );
    };
}

// Clear out the store on logout, need this to prevent unwanted user states
export const metaReducers: MetaReducer<any>[] = [logout];

@NgModule({
    imports: [BrowserModule,
        FormsModule,
        BrowserAnimationsModule,
        ReactiveFormsModule,
        LoginModule,
        CoreModule,
        RestModule.forRoot({ useConfigSvc: true, interceptors: [withClToken2AsCookieInterceptor()] }),
        FlexLayoutModule,
        AppRoutingModule,
        StoreModule.forRoot(reducers, {
            metaReducers,
            runtimeChecks: {
                strictStateImmutability: true,
                strictActionImmutability: true,
            },
        }),
        EffectsModule.forRoot(effects),
        StoreRouterConnectingModule.forRoot(),
        extModules,
        FileUploadModule,
        ClipboardModule,
        QuillModule.forRoot(),
        TrimInputDirective,
        OwlDateTimeModule,
        OwlNativeDateTimeModule,
        OwlMomentDateTimeModule,
        NgIdleModule.forRoot(),
    ],
    declarations: [AppComponent],
    providers: [
        {
            // Angular by default wants to use RouterStateSerializer,
            // but we tell it to use our CustomSerializer
            provide: RouterStateSerializer,
            useClass: CustomSerializer,
        },
        MatIconRegistry,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthHttpInterceptor,
            multi: true,
        },
        {
            provide: ErrorHandler,
            useClass: CacheErrorInterceptor,
        },
        { provide: TRANSLOCO_TRANSPILER, useClass: DefaultTranspiler },
        { provide: TRANSLOCO_MISSING_HANDLER, useClass: DefaultMissingHandler },
        { provide: TRANSLOCO_INTERCEPTOR, useClass: DefaultInterceptor },
        { provide: TRANSLOCO_FALLBACK_STRATEGY, useClass: DefaultFallbackStrategy },
        LocalStorage,
        TimezoneHelper,
        StatesHelper,
        LanguageHelper,
        UsherPayloadHelper,
        DurationFormat,
        ProficiencyReturn,
        LocalStorageCache,
        LoggerService,
        { provide: ENV, useValue: environment },
        provideHttpClient(withInterceptorsFromDi()),
    ],
    bootstrap: [AppComponent],
})
export class AppModule {
    constructor(
        public matIconRegistry: MatIconRegistry,
        sanitizer: DomSanitizer
    ) {
        matIconRegistry.registerFontClassAlias("fontawesome", "fa");
        matIconRegistry.addSvgIconSet(
            sanitizer.bypassSecurityTrustResourceUrl(
                "/assets/images/icons/iconSets/passportIcons.svg"
            )
        );
    }
}
